/**
 * Permission Utility Functions
 *
 * IMPORTANT: There is NO traditional admin role in this system.
 * Admin access is determined by: employee has an entry in teams[] where team === "high_potit"
 * The backward-compat `employee.team` string is still supported for JWT-derived objects.
 *
 * Any employee in the high_potit team has full admin privileges.
 */

/**
 * Check if employee is an admin (has high_potit in their teams)
 * @param {Object} employee - The employee object
 * @returns {boolean} - True if employee is in high_potit team
 */
export const isAdmin = (employee) => {
  if (!employee) return false;
  // Prefer full teams array; fall back to backward-compat team string
  if (Array.isArray(employee.teams) && employee.teams.length > 0) {
    return employee.teams.some((t) => t.team === "high_potit");
  }
  return employee.team === "high_potit";
};

/**
 * Check if employee can manage all employees (admin permission)
 * @param {Object} employee - The employee object
 * @returns {boolean} - True if employee can manage all employees
 */
export const canManageAllEmployees = (employee) => {
  return isAdmin(employee);
};

/**
 * Check if employee can view all requests (admin permission)
 * @param {Object} employee - The employee object
 * @returns {boolean} - True if employee can view all requests
 */
export const canViewAllRequests = (employee) => {
  return isAdmin(employee);
};

/**
 * Check if employee can approve/reject requests (admin permission)
 * @param {Object} employee - The employee object
 * @returns {boolean} - True if employee can approve/reject requests
 */
export const canApproveRequests = (employee) => {
  return isAdmin(employee);
};

/**
 * Check if employee can create requests for others (admin permission)
 * @param {Object} employee - The employee object
 * @returns {boolean} - True if employee can create requests for others
 */
export const canCreateRequestsForOthers = (employee) => {
  return isAdmin(employee);
};

/**
 * Check if employee can use random WFH feature (admin permission)
 * @param {Object} employee - The employee object
 * @returns {boolean} - True if employee can use random WFH
 */
export const canUseRandomWFH = (employee) => {
  return isAdmin(employee);
};

/**
 * Check if employee can export HR data (admin permission)
 * @param {Object} employee - The employee object
 * @returns {boolean} - True if employee can export HR data
 */
export const canExportHRData = (employee) => {
  return isAdmin(employee);
};

/**
 * Check if employee can edit a specific request
 * @param {Object} employee - The employee object
 * @param {Object} request - The request object
 * @returns {boolean} - True if employee can edit the request
 */
export const canEditRequest = (employee, request) => {
  if (!employee || !request) return false;
  // Admins can edit any request
  if (isAdmin(employee)) return true;
  // Normal employees can only edit their own requests
  return (
    request.employeeId === employee._id || request.employeeId === employee.id
  );
};

/**
 * Check if employee can view a specific request
 * @param {Object} employee - The employee object
 * @param {Object} request - The request object
 * @returns {boolean} - True if employee can view the request
 */
export const canViewRequest = (employee, request) => {
  if (!employee || !request) return false;
  // Admins can view all requests
  if (isAdmin(employee)) return true;
  // Normal employees can only view their own requests
  return (
    request.employeeId === employee._id || request.employeeId === employee.id
  );
};
